import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuidV4 } from 'uuid';
import { Auth } from 'components/Auth/Auth';
import { useApiService } from '../../../contexts/ApiServiceContext/ApiServiceContext';
import { type AudioConfig } from '../../../dorian-shared/types/media/MediaConfigs';
import { User } from '../../../dorian-shared/types/user/User';
import { bugTracker } from '../../../services/bugTracker/BugTrackerService';
import { convertAudio } from '../../../services/ffmpeg';
import { api } from '../../api';
import { isAdminRole } from '../../utils/userActionUtils';
import {
  AudioType,
  FileToUpload,
  SelectedFilesToUpload,
  UploadFilesModalStatus,
} from './types';
import { getUploadFileHash } from './utils';

export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
export const MAX_FILES_ADMIN = 100;
export const MAX_FILES_BASIC = 10;

const detectAudioTypeFromFilename = (filename: string): AudioType | null => {
  const lower = filename.toLowerCase();
  if (lower.includes('_sfx')) return 'effect';
  if (lower.includes('_music')) return 'music';
  return null;
};

export function useUploadFilesAudio(
  onUploaded?: (files: FileToUpload[]) => void,
) {
  const apiService = useApiService();
  const abortControllerRef = useRef<AbortController | null>(null);

  const [filesToUpload, setFilesToUpload] = useState<FileToUpload[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadFinished, setIsUploadFinished] = useState(false);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [audioConfig, setAudioConfig] = useState<AudioConfig | null>(null);

  const user = useMemo(() => new Auth().getUser(), []);
  const maxFiles = useMemo(() => (isAdminRole(user) ? MAX_FILES_ADMIN : MAX_FILES_BASIC), [user]);

  useEffect(() => {
    setAuthUser(user);
  }, [user]);

  useEffect(() => {
    setIsLoading(true);
    apiService.fetchAudioConfig()
      .then((config) => {
        if (config) {
          setAudioConfig(config);
        }
      })
      .catch((error) => {
        bugTracker().reportError(error);
      })
      .finally(() => setIsLoading(false));
  }, [apiService]);

  useEffect(() => {
    if (maxFiles && filesToUpload.length > maxFiles) {
      setErrorMessage(`You can upload a maximum of ${maxFiles} files at a time`);
    } else {
      setErrorMessage(null);
    }
  }, [filesToUpload.length, maxFiles]);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);

  const handleDropZoneChange = useCallback(async (files: SelectedFilesToUpload[]) => {
    const existedAliases: string[] = [];
    const newValues = files.map(async (file) => {
      const name = file.file.name.replace(/\.[^/.]+$/, '');
      const aliasName = await getUploadFileHash(file.file, authUser?.id ?? 0);
      const audioType = detectAudioTypeFromFilename(file.file.name);

      const isAlreadyInList = existedAliases.includes(aliasName);

      const uniqueId = uuidV4();

      if (file.file.size > MAX_FILE_SIZE) {
        return {
          ...file,
          id: 0,
          uniqueId,
          name,
          aliasName,
          imageUrl: '',
          audioType: null,
          status: UploadFilesModalStatus.Error,
          errors: [{ message: `Maximum file size is ${MAX_FILE_SIZE_MB}MB`, code: 'error' }],
        } as FileToUpload;
      }

      if (isAlreadyInList) {
        return {
          ...file,
          id: 0,
          uniqueId,
          name,
          aliasName,
          imageUrl: '',
          audioType: null,
          status: UploadFilesModalStatus.Error,
          errors: [{ message: 'Already on the list. Duplicated file.', code: 'error' }],
        } as FileToUpload;
      }

      existedAliases.push(aliasName);

      return {
        ...file,
        id: 0,
        uniqueId,
        name,
        aliasName,
        imageUrl: '',
        audioType,
      } as FileToUpload;
    });

    const newValuesResolved = await Promise.all(newValues);
    setFilesToUpload(newValuesResolved);
  }, [authUser?.id]);

  const updateAudioType = useCallback((aliasName: string, audioType: AudioType) => {
    setFilesToUpload((prev) => prev.map((f) => (f.aliasName === aliasName ? { ...f, audioType } : f)));
  }, []);

  const updateStatus = useCallback((alias: string, status: UploadFilesModalStatus | 'Converting') => {
    setFilesToUpload((prev) => prev.map((f) => (f.aliasName === alias
      ? { ...f, status: status as UploadFilesModalStatus }
      : f)));
  }, []);

  const updateProgress = useCallback((alias: string, progress: number) => {
    setFilesToUpload((prev) => prev.map((f) => (f.aliasName === alias ? { ...f, progress } : f)));
  }, []);

  const updateStatusError = useCallback((alias: string, message: string) => {
    setFilesToUpload((prev) => prev.map((f) => (f.aliasName === alias
      ? { ...f, status: UploadFilesModalStatus.Error, errors: [{ message, code: 'error' }] }
      : f)));
  }, []);

  const updateFile = useCallback((alias: string, file: File) => {
    setFilesToUpload((prev) => prev.map((f) => (f.aliasName === alias ? { ...f, file } : f)));
  }, []);

  const processAndUploadFile = useCallback(async (fileToUpload: FileToUpload): Promise<void> => {
    if (!audioConfig) {
      updateStatusError(fileToUpload.aliasName, 'Audio config not loaded');
      return;
    }

    // Step 1: Convert audio
    updateStatus(fileToUpload.aliasName, 'Converting');
    updateProgress(fileToUpload.aliasName, 0);

    abortControllerRef.current = new AbortController();

    let convertedAudio: Blob;
    try {
      convertedAudio = await convertAudio(
        fileToUpload.file,
        audioConfig,
        (progress) => {
          updateProgress(fileToUpload.aliasName, Math.floor(progress * 100));
        },
        abortControllerRef.current.signal,
      );
    } catch (error) {
      updateStatusError(fileToUpload.aliasName, (error as Error).message || 'Audio conversion failed');
      return;
    }

    // Step 2: Upload audio
    updateStatus(fileToUpload.aliasName, UploadFilesModalStatus.Uploading);
    updateProgress(fileToUpload.aliasName, 0);

    const alias = uuidV4();
    const formData = new FormData();
    formData.append('audio', convertedAudio, `${alias}.mp3`);
    formData.append('alias', alias);
    formData.append('title', fileToUpload.name);
    formData.append('type', fileToUpload.audioType ?? 'effect');
    formData.append('policyId', '2');

    try {
      const response = await api.post('/v1/audio', formData, {
        headers: {
          'content-type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            updateProgress(fileToUpload.aliasName, percentCompleted);
          }
        },
      });

      if (!response.data.success) {
        updateStatusError(
          fileToUpload.aliasName,
          response.data.error || response.data.message || 'Upload failed',
        );
        return;
      }

      updateStatus(fileToUpload.aliasName, UploadFilesModalStatus.Uploaded);
    } catch (error) {
      updateStatusError(fileToUpload.aliasName, 'Error occurred while uploading audio');
    }
  }, [audioConfig, updateProgress, updateStatus, updateStatusError]);

  const handleUploadClick = useCallback(async () => {
    if (maxFiles && filesToUpload.length > maxFiles) {
      setErrorMessage(`You can upload a maximum of ${maxFiles} files at a time`);
      return;
    }

    if (!authUser) {
      setErrorMessage('You need to be logged in to upload files');
      return;
    }

    // Validate all files have audioType selected
    const filesWithoutType = filesToUpload.filter(
      (f) => f.status === UploadFilesModalStatus.Ready && !f.audioType,
    );
    if (filesWithoutType.length > 0) {
      setErrorMessage('Please select audio type for all files');
      return;
    }

    if (!audioConfig) {
      setErrorMessage('Audio config not loaded. Please try again.');
      return;
    }

    setErrorMessage(null);
    setIsLoading(true);

    // Process files sequentially (FFmpeg limitation - one at a time)
    const readyFiles = filesToUpload.filter((f) => f.status === UploadFilesModalStatus.Ready);

    for (const fileToUpload of readyFiles) {
      await processAndUploadFile(fileToUpload);
    }

    setIsUploadFinished(true);
    setIsLoading(false);
  }, [authUser, audioConfig, filesToUpload, maxFiles, processAndUploadFile]);

  const handleDelete = useCallback((fileToUpload: FileToUpload) => {
    setFilesToUpload((prev) => prev.filter((file) => file !== fileToUpload));
  }, []);

  const manageListCallbacks = useMemo(() => ({
    onDelete: handleDelete,
    onUploadFinished: (files: FileToUpload[]) => {
      onUploaded?.(files);
    },
  }), [handleDelete, onUploaded]);

  // Check if upload button should be disabled
  const hasFilesWithoutType = filesToUpload.some(
    (f) => f.status === UploadFilesModalStatus.Ready && !f.audioType,
  );
  const hasErrorStatus = filesToUpload.some((f) => f.status === UploadFilesModalStatus.Error);

  return {
    isLoading,
    setIsLoading,
    filesToUpload,
    handleDropZoneChange,
    manageListCallbacks,
    handleUploadClick,
    isUploadFinished,
    errorMessage,
    updateAudioType,
    hasFilesWithoutType,
    hasErrorStatus,
  };
}
